/**
 * useQuoteSourceDevice — chains the work-item fetch and the check-in
 * fetch to resolve the specific check-in device a repair quote is for.
 *
 * The repair quote stores two pieces of linkage:
 *  - `sm4rtJobId` (existing)            → identifies the work item
 *  - `sourceCheckInDraftJobId` (new)    → identifies which device on
 *                                          that work item's check-in
 *
 * The full inspection payload is NOT snapshotted onto the quote — it's
 * resolved live from the check-in each time the quote page loads, so
 * device details on the quote always reflect the latest inspection.
 */

import { useMemo } from 'react';
import type {
  CheckInDraftJob,
  WorkItem,
} from '@techgitter/contracts';

import { useWorkItem } from './useWorkItemData';
import { useCheckInSession } from './useCheckInSession';

export interface UseQuoteSourceDeviceResult {
  workItem: WorkItem | null;
  /** Devices available on the linked check-in. Empty when there's no
   *  work item, no linked check-in, or no draft jobs. */
  availableDevices: CheckInDraftJob[];
  /** The resolved device when `sourceCheckInDraftJobId` matches one of
   *  the available devices. Null when nothing is selected yet or the
   *  saved selection no longer exists (e.g. the draft job was
   *  deleted). */
  device: CheckInDraftJob | null;
  isLoading: boolean;
  error: string | null;
}

export function useQuoteSourceDevice(params: {
  sm4rtJobId: string | null;
  sourceCheckInDraftJobId: string | null;
}): UseQuoteSourceDeviceResult {
  const { sm4rtJobId, sourceCheckInDraftJobId } = params;

  // `useWorkItem` filters the list response by `WorkItem.id`. Quotes
  // store `sm4rtJobId`, which is the same value used as the id on
  // job-typed work items, so passing it through works. When no link
  // exists we pass an empty string — the hook short-circuits.
  const { workItem, isLoading: wiLoading, error: wiError } = useWorkItem(
    sm4rtJobId ?? '',
  );

  const sessionId = workItem?.checkInSessionGuid ?? null;
  const { session, isLoading: ciLoading, error: ciError } = useCheckInSession(
    sessionId,
  );

  const availableDevices = useMemo<CheckInDraftJob[]>(() => {
    return session?.draftJobs ?? [];
  }, [session]);

  const device = useMemo<CheckInDraftJob | null>(() => {
    if (!sourceCheckInDraftJobId) return null;
    return (
      availableDevices.find((d) => d.id === sourceCheckInDraftJobId) ?? null
    );
  }, [availableDevices, sourceCheckInDraftJobId]);

  // Don't surface "loading work item" once we know there's no link to
  // resolve — keeps the UI from showing a spinner forever on quotes
  // that aren't tied to a work item.
  const isLoading = sm4rtJobId
    ? wiLoading || (Boolean(sessionId) && ciLoading)
    : false;

  return {
    workItem: workItem ?? null,
    availableDevices,
    device,
    isLoading,
    error: wiError ?? ciError ?? null,
  };
}
